import {
  queryOptions,
  useQuery,
  type UseQueryResult,
} from "@tanstack/react-query";

import type { ApiClient } from "@/api/client";
import { useApiClient } from "@/api/client-context";
import { fetchProcessInstance } from "@/api/endpoints/fetch-process-instance";
import { toModifiedProcessModelId } from "@/api/identifiers";
import { qk } from "@/api/query-keys";
import type { CaseRef } from "@/features/case-detail/use-case-detail";

import {
  deriveDiagramMarks,
  type DiagramMarks,
  type DiagramTaskState,
} from "./derive-diagram-marks";

export type CaseDiagram = {
  /** BPMN XML。取得できなければ null。 */
  readonly xml: string | null;
  readonly marks: DiagramMarks;
};

type SpiffTaskInfoRow = {
  readonly bpmn_identifier?: string | null;
  readonly task_definition_identifier?: string | null;
  readonly state?: string | null;
  readonly task_status?: string | null;
};

const NO_MARKS: DiagramMarks = { current: [], completed: [], error: [] };

/**
 * 案件の図（BPMN XML）と、どこを通ったかのハイライト。
 *
 * XML は詳細 API に同梱されるので専用のファイル取得は要らない。
 * 状態の取得（task-info）は権限次第で失敗し得るため、その場合は
 * ハイライト無しで図だけ出す（図が出ないより情報量が多い）。
 */
export function caseDiagramQueryOptions(client: ApiClient, ref: CaseRef) {
  const modifiedId = toModifiedProcessModelId(ref.processModelId);

  const fetchDiagram = async (): Promise<CaseDiagram> => {
    const [detail, tasks] = await Promise.all([
      // for-me は関与者しか通らない。全案件モニタから開く場合は管理者経路に落ちる
      fetchProcessInstance<{
        readonly bpmn_xml_file_contents?: string | null;
        readonly status?: string | null;
      }>(client, modifiedId, ref.caseId),
      client
        .get<unknown>(
          `/process-instances/for-me/${modifiedId}/${ref.caseId}/task-info`,
        )
        .catch(() => []),
    ]);

    return {
      xml: detail.bpmn_xml_file_contents ?? null,
      // 失敗したタスクは ERROR にならないので、案件のステータスから判断する
      marks: toMarks(tasks, detail.status === "error"),
    };
  };

  return queryOptions({
    queryKey: [
      ...qk.processInstances.detail(modifiedId, ref.caseId),
      "diagram",
    ],
    queryFn: fetchDiagram,
    staleTime: 30_000,
  });
}

function toMarks(tasks: unknown, isErrored: boolean): DiagramMarks {
  const rows = Array.isArray(tasks)
    ? (tasks as readonly SpiffTaskInfoRow[])
    : ((tasks as { readonly results?: readonly SpiffTaskInfoRow[] })?.results ??
      []);

  if (rows.length === 0) return NO_MARKS;

  const states: DiagramTaskState[] = rows.map((row) => ({
    bpmnIdentifier:
      row.bpmn_identifier ?? row.task_definition_identifier ?? null,
    state: row.state ?? row.task_status ?? null,
  }));

  return deriveDiagramMarks(states, { isErrored });
}

export function useCaseDiagram(
  ref: CaseRef | null,
): UseQueryResult<CaseDiagram> {
  const client = useApiClient();
  return useQuery({
    ...caseDiagramQueryOptions(
      client,
      ref ?? { caseId: 0, processModelId: "" },
    ),
    enabled: ref !== null,
  });
}
